import { seededRandom } from '../core/board';

export interface ViewFrame { zoom: number; focal: [number, number] }
export interface RewardView { id: string; portrait: ViewFrame; landscape: ViewFrame }
export interface RewardViewState { current?: { boardId: string; sceneId: string; viewId: string }; recent: string[] }
const frame = (zoom: number, x: number, y: number): ViewFrame => ({ zoom, focal: [x, y] });
const view = (id: string, portrait: ViewFrame, landscape: ViewFrame): RewardView => ({ id, portrait, landscape });
const wide = view('whole', frame(1, .5, .5), frame(1, .5, .5));
const catalog: Record<string, RewardView[]> = {
  'forest-dawn': [wide,
    view('sunbeams', frame(1.45, .7, .3), frame(1.4, .65, .35)),
    view('stream', frame(1.5, .56, .55), frame(1.5, .55, .65)),
    view('ferns', frame(1.5, .25, .82), frame(1.45, .25, .8))],
  'ocean-cove': [wide,
    view('turtle', frame(1.55, .5, .62), frame(1.5, .5, .6)),
    view('sunlit-water', frame(1.45, .7, .3), frame(1.4, .7, .35)),
    view('shoreline', frame(1.5, .3, .85), frame(1.45, .25, .8))],
  'cosmos-wonder': [wide,
    view('rings', frame(1.45, .5, .35), frame(1.45, .5, .3)),
    view('nebula', frame(1.5, .25, .18), frame(1.5, .2, .2)),
    view('icy-horizon', frame(1.5, .5, .8), frame(1.5, .6, .8))],
  'garden-morning': [wide,
    view('butterfly', frame(1.55, .62, .4), frame(1.55, .5, .5)),
    view('flowers', frame(1.5, .62, .8), frame(1.45, .78, .75)),
    view('garden-path', frame(1.45, .4, .45), frame(1.45, .4, .55))],
  'kitchen-sunlight': [wide,
    view('bread', frame(1.45, .62, .5), frame(1.45, .56, .5)),
    view('basil-tomatoes', frame(1.5, .2, .45), frame(1.45, .18, .55)),
    view('sunlit-linen', frame(1.5, .75, .8), frame(1.45, .8, .75))],
};

export const rewardViews = (sceneId: string): readonly RewardView[] => catalog[sceneId] ?? [];
const validKey = (key: unknown): key is string => typeof key === 'string'
  && Object.entries(catalog).some(([sceneId, views]) => views.some(view => key === `${sceneId}:${view.id}`));

export function recoverRewardViews(value: unknown): RewardViewState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { recent: [] };
  const state = value as RewardViewState, current = state.current;
  const recent = Array.isArray(state.recent) ? state.recent.slice(-64).filter(validKey) : [];
  return { recent, ...(current && typeof current.boardId === 'string' && current.boardId.length <= 128
    && validKey(`${current.sceneId}:${current.viewId}`) ? { current: { boardId: current.boardId, sceneId: current.sceneId, viewId: current.viewId } } : {}) };
}

export function chooseRewardView(state: RewardViewState, boardId: string, sceneId: string): { state: RewardViewState; view?: RewardView } {
  const views = rewardViews(sceneId);
  const current = state.current;
  const pinned = current?.boardId === boardId && current.sceneId === sceneId ? views.find(view => view.id === current.viewId) : undefined;
  if (pinned) return { state, view: pinned };
  if (!views.length) return { state };
  const recent = state.recent.filter(key => key.startsWith(`${sceneId}:`)).slice(-2);
  const available = views.filter(view => !recent.includes(`${sceneId}:${view.id}`));
  const weighted = available.flatMap(view => view.id === 'whole' ? [view] : [view, view]);
  const selected = weighted[Math.floor(seededRandom(`${boardId}:${sceneId}`)() * weighted.length)]!;
  return { state: { current: { boardId, sceneId, viewId: selected.id }, recent: [...state.recent, `${sceneId}:${selected.id}`].slice(-64) }, view: selected };
}
